/** Formatting helpers — one implementation each (UI guide §8). */

export function formatCurrency(n: number): string {
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US')}`;
}

/** Money with cents, for the procedure table and the amount it totals to. */
export function formatCents(n: number): string {
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** One quoted line, as far as the money goes. */
export interface QuoteLine {
  officeFee: number;
  allowed: number;
  insurance: number;
}

/**
 * What the patient owes on one in-network line: the allowed amount less what
 * the plan pays. Never the office fee (the difference is the write-off, D157).
 */
export function patientOwes(line: { allowed: number; insurance: number }): number {
  return Math.max(0, line.allowed - line.insurance);
}

export interface QuoteTotals {
  fee: number;
  allowed: number;
  insurance: number;
  patient: number;
}

/** Totals computed from the rows, so a total can never disagree with its rows. */
export function quoteTotals(rows: readonly QuoteLine[]): QuoteTotals {
  return rows.reduce<QuoteTotals>(
    (acc, row) => ({
      fee: acc.fee + row.officeFee,
      allowed: acc.allowed + row.allowed,
      insurance: acc.insurance + row.insurance,
      patient: acc.patient + patientOwes(row),
    }),
    { fee: 0, allowed: 0, insurance: 0, patient: 0 },
  );
}

const WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];

/** Small counts as words, as the report writes them ("six procedures"). */
export function countWord(n: number): string {
  return WORDS[n] ?? String(n);
}

/** The patient's first name, for copy like "Terence pays". */
export function firstName(full: string): string {
  return full.split(' ')[0] ?? full;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return `${MONTHS[d.getMonth()] ?? ''} ${String(d.getDate())}, ${String(d.getFullYear())}`;
}

/** Days from today to an ISO date, computed — never stored. */
export function daysUntil(iso: string, todayIso: string): number {
  const a = new Date(`${iso}T00:00:00`).getTime();
  const b = new Date(`${todayIso}T00:00:00`).getTime();
  return Math.max(0, Math.round((a - b) / 86400000));
}
